import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, getIn } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import icLocked from 'core/assets/svg/icLocked.svg';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { Tooltip } from 'core/components/tooltip';
import { NotificationCard } from 'core/components/notification-card';
import useSession from 'core/hooks/use-session';
import { getDraftStatus } from 'core/helpers/policy-drafting';
import { PolicyChangeAction } from 'core/helpers/policy-status';
import { getButtonLabel, getDraftActionOptions, getDraftActionButtonLabel, DRAFT_ACTION } from '../helpers';

import styles from './policy-change-actions.styles';

function PolicyChangeActions({ classes, hasDraftActions, onClose, disableInternalNote, action, preview }) {
  const { values, isSubmitting } = useFormikContext();
  const { canApproveUWReview } = useSession();

  const actionValue = getIn(values, 'farmPolicyAction');

  if (hasDraftActions) {
    const options = getDraftActionOptions({
      draftStatus: getDraftStatus(preview),
      needsUnderwritingReview: preview.needsUnderwritingReview,
      canApproveUWReview
    }).map(({ label, lockedReason, ...option }) => ({
      ...option,
      label: lockedReason ? (
        <span className={classes.lockedOptionLabel}>
          {label}
          <Tooltip text={lockedReason} smallIcon={{ src: icLocked }} className={classes.lockIcon} placement="top" />
        </span>
      ) : (
        label
      )
    }));

    return (
      <Grid item xs={12} className={classes.farmPolicyContainer}>
        <Card type="primaryEditable" className={classes.decisionCard}>
          <Label className={classes.decisionCardTitle}>What would you like to do next?</Label>
          <Label className={classes.decisionCardPrompt}>Please select an option</Label>
          <FormField
            id="farmPolicyAction"
            name="farmPolicyAction"
            type="radio"
            options={options}
            optionsSideBySide={false}
            showDividers
            className={classes.decisionCardRadioGroup}
            ignoreGlobalDisabledState
            showError={false}
          />
          {actionValue === DRAFT_ACTION.COMPLETE && (
            <>
              <FormField
                id="internalNote"
                name="internalNote"
                type="string"
                label="Add an internal note"
                mode="light"
                xs={12}
                ignoreGlobalDisabledState
                permissions={{ isLicensedAction: false }}
                disabled={disableInternalNote}
              />
              <NotificationCard className={classes.notification} iconSize="icon16" type="light" noShadow>
                <Label>Completing this change will remove all other working drafts on this policy.</Label>
              </NotificationCard>
            </>
          )}
          <div className={classes.decisionCardActions}>
            <Button type="button" variant="contained" className={classes.decisionCardCancelButton} onClick={onClose}>
              Cancel
            </Button>
            {/* #35452: disable while a confirm is in flight so a double-click can't fire a second
                confirmPolicyChange (which, before the fix, double-billed/refunded the member). */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.decisionCardSubmitButton}
              disabled={!actionValue || isSubmitting || preview.estimateOnly}
            >
              {getDraftActionButtonLabel(actionValue)}
            </Button>
          </div>
        </Card>
      </Grid>
    );
  }

  return (
    <>
      <Grid item xs={12}>
        <div className={classes.noteRow}>
          <FormField
            id="internalNote"
            name="internalNote"
            type="string"
            label="Add an internal note"
            mode="light"
            ignoreGlobalDisabledState
            permissions={{ isLicensedAction: false }}
            disabled={disableInternalNote}
          />
          {/* #35452: disable while a confirm is in flight to prevent a double-click double-submit. */}
          <Button
            id="submit"
            name="submit"
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || preview.estimateOnly}
          >
            {getButtonLabel(action)}
          </Button>
        </div>
      </Grid>
    </>
  );
}

PolicyChangeActions.propTypes = {
  classes: PropTypes.object.isRequired,
  hasDraftActions: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  disableInternalNote: PropTypes.bool,
  action: PropTypes.oneOf([...Object.values(PolicyChangeAction), '']),
  preview: PropTypes.object.isRequired
};

PolicyChangeActions.defaultProps = {
  disableInternalNote: false,
  action: ''
};

export default withStyles(styles)(PolicyChangeActions);
